import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faSearch,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faCommentDots,
  faFileAlt,
  faUser,
  faUsers,
  faBookOpen,
  faCalendarAlt,
  faFilePdf,
  faChartBar,
  faClock,
  faGraduationCap,
  faStar,
  faChevronDown,
  faChevronUp,
  faDownload,
  faEye,
  faBrain,
  faBullseye,
  faLightbulb,
  faTimes,
  faImage,
  faSyncAlt,
  faInfoCircle,
  faAward,
} from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { getApiUrl, SERVERS, ENDPOINTS } from "../config/api";
import "./Review.css";

// Helper component for expandable text (copied from Analysis page)
const JustificationWithToggle = ({ text, charLimit = 180 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span>No justification available</span>;
  if (text.length <= charLimit) return <span>{text}</span>;
  return (
    <span>
      {expanded ? text : text.slice(0, charLimit) + "..."}
      <button
        className="show-more-btn"
        style={{
          marginLeft: 8,
          fontSize: "0.9em",
          background: "none",
          border: "none",
          color: "#ffd700",
          cursor: "pointer",
          textDecoration: "underline",
        }}
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </span>
  );
};

const Review = () => {
  const PROFESSOR_EMAIL = "shivani.pandita@wilp.bits-pilani.ac.in";

  // Helper functions for time formatting and colors (copied from Analysis page)
  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getQualityScoreColor = (score) => {
    if (score >= 8) return "#10b981";
    if (score >= 6) return "#f59e0b";
    if (score >= 4) return "#ef4444";
    return "#6b7280";
  };

  const getTimeEfficiencyColor = (efficiency) => {
    switch (efficiency) {
      case "Too Much":
        return "#F59E0B";
      case "Too Little":
        return "#EF4444";
      default:
        return "#6B7280";
    }
  };

  // Selection states for review context
  const [professorEmails, setProfessorEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false); // Track filters collapse state
  const [rubricsExpanded, setRubricsExpanded] = useState(false); // Track rubrics collapse state
  const [customFields, setCustomFields] = useState([]); // Dynamic custom fields
  const [customFieldFilters, setCustomFieldFilters] = useState({}); // Custom field filter values

  // Form data with filters (now all optional except rubrics)
  const [formData, setFormData] = useState({
    instructorName: '',
    academicYear: '',
    semester: '',
    programCategory: '',
    programCommittee: '',
    courseCode: '',
    reviewStatus: '',  // New filter for review progress
    rubricName: 'Generic Rubric',
    paperRubricName: '',  // Will be set dynamically when rubrics load
    examType: '',
    examFormat: '',
    uploaderName: '',
  })
  
  // Analysis states (copied from Analysis page)
  const [questionPapers, setQuestionPapers] = useState([])
  const [selectedPaper, setSelectedPaper] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisResults, setAnalysisResults] = useState(null)
  const [expandedQuestions, setExpandedQuestions] = useState({})
  const [error, setError] = useState('')
  const [questionPdfUrls, setQuestionPdfUrls] = useState({})
  const [loadingPdfs, setLoadingPdfs] = useState({})
  const [failedPdfs, setFailedPdfs] = useState({})
  const [cacheInfo, setCacheInfo] = useState(null)
  const [rubrics, setRubrics] = useState([])
  const [paperRubrics, setPaperRubrics] = useState([])  // Separate list for paper level rubrics
  const [handoutWarning, setHandoutWarning] = useState('')
  const [authLoading, setAuthLoading] = useState(true);
  const [dataerror, setDataerror] = useState(null);

  const [userData, setUserData] = useState({});
  // Review-specific states
  const [paperReview, setPaperReview] = useState(null);
  const [questionReviews, setQuestionReviews] = useState({});
  const [paperLevelFeedback, setPaperLevelFeedback] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [savingQuestionFeedback, setSavingQuestionFeedback] = useState({}); // Track which questions are being saved
  const [savingPaperFeedback, setSavingPaperFeedback] = useState(false); // Track if paper feedback is being saved
  const [downloadingReport, setDownloadingReport] = useState(false);
  const savingFeedbackTimeout = useRef(null);
  const savingPaperFeedbackTimeout = useRef(null);
  const reportContainerRef = useRef(null);
    const [uploadResults, setUploadResults] = useState(null)
    const [samlData, setSamlData] = useState({});
  // Academic years (same as Analysis page)
  const academicYears = [];
  const [examTypes, setExamTypes] = useState([]);
  const [examFormats, setExamFormats] = useState([]);
  const [uploaders, setUploaders] = useState([]);

  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 10; i++) {
    const year = currentYear - i;
    academicYears.push(`${year}-${(year + 1).toString().slice(-2)}`);
  }
  useEffect(() => {
  const fetchSamlData = async () => {
    try {
      // IMPORTANT: use exact backend path (qpra not qbg)
      const url = `/qpra/get-saml-data`;
      console.log("[SAML] Fetching SAML data from:", url);

      const response = await fetch(url, {
        method: "GET",
        credentials: "include", // include HttpOnly cookie
        headers: {
          "Accept": "application/json",
        },
      });

      if (!response.ok) {
        console.warn("[SAML] fetch returned non-ok:", response.status);
        // If unauthorized, redirect to login (start SAML flow)
        if (response.status === 401) {
          console.log("[SAML] Session missing/expired — redirecting to login");
          window.location.href = `/qprs/login`;
          return;
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setUserData(data);
      // Defensive extraction
      setSelectedEmail(data.email)
      const courseData = {};
      const apiData = data?.api_data;

      if (Array.isArray(apiData)) {
        apiData.forEach((source, srcIndex) => {
          const ds = source?.["data-source"] || source?.data_source || `source_${srcIndex}`;
          const courses = Array.isArray(source?.courses) ? source.courses : Array.isArray(source?.data) ? source.data : [];

          courses.forEach((course) => {
            if (!course || typeof course !== "object") return;

            if (ds === "taxila") {
              // Taxila: key = fullname, value = shortname
              if (course.fullname && course.shortname) {
                courseData[course.fullname] = course.shortname;
              }
            } else if (ds === "canvas") {
              // Canvas: key = name, value = course_code
              if (course.name && course.course_code) {
                courseData[course.name] = course.course_code;
              }
            } else {
              // generic fallback — try common fields
              const title = course.fullname || course.name || course.title;
              const code = course.shortname || course.course_code || course.code || course.id;
              if (title && code) courseData[title] = code;
            }
          });
        });
      } else {
        console.warn("[SAML] api_data missing or not an array:", apiData);
      }

      setSamlData(courseData);
      setError(null);
    } catch (err) {
      console.error("Error fetching SAML data:", err);
      setError(err.message || "Unknown error");
      setSamlData({}); // ensure consistent state shape
    }
  };

  fetchSamlData();
}, []);

  // Load professor emails on component mount and handle cleanup
  useEffect(() => {
    loadProfessorEmails();
    return () => {
      // Cleanup any pending save timeouts
      if (savingFeedbackTimeout.current) {
        clearTimeout(savingFeedbackTimeout.current);
      }
      if (savingPaperFeedbackTimeout.current) {
        clearTimeout(savingPaperFeedbackTimeout.current);
      }
    };
  }, []);

  // Load custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.CUSTOM_FIELDS);
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch custom fields");

        const data = await response.json();
        // Handle both array and object responses
        const fieldsArray = Array.isArray(data) ? data : (data.fields || []);
        const activeFields = fieldsArray
          .filter(field => field.is_active && field.field_type === 'select') // Only show select fields for filtering
          .sort((a, b) => a.display_order - b.display_order);
        
        setCustomFields(activeFields);
        
        // Initialize custom field filter values
        const initialFilters = {};
        activeFields.forEach(field => {
          initialFilters[field.field_name] = '';
        });
        setCustomFieldFilters(initialFilters);
      } catch (err) {
        console.error("Error fetching custom fields:", err);
        setCustomFields([]);
        setCustomFieldFilters({});
      }
    };

    fetchCustomFields();
  }, []);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        // Exam Types
        const typesRes = await fetch(getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_ALL_EXAM_TYPES));
        const typesData = await typesRes.json();
        setExamTypes(typesData.exam_types || []);

        // Exam Formats
        const formatsRes = await fetch(getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_ALL_EXAM_FORMATS));
        const formatsData = await formatsRes.json();
        setExamFormats(formatsData.exam_formats || []);

        // Uploaders
        const uploadersRes = await fetch(getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_ALL_UPLOADERS));
        const uploadersData = await uploadersRes.json();
        setUploaders(uploadersData.uploaders || []);

      } catch (err) {
        console.error("Error loading filters:", err);
      }
    };

    fetchFilters();
  }, []);


  // Load assigned courses when email changes
  useEffect(() => {
    if (selectedEmail) {
      loadAssignedCourses();
    } else {
      setAssignedCourses([]);
      setFormData(prev => ({ ...prev, courseCode: '' }));
    }
  }, [selectedEmail]);

  // Auto-fetch question papers when selectedEmail or filters change
  useEffect(() => {
    if (selectedEmail) {
      fetchQuestionPapers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmail, formData.academicYear, formData.semester, formData.courseCode, formData.reviewStatus, formData.examType, formData.examFormat, formData.uploaderName, customFieldFilters]);


  // Fetch rubrics on component mount
  useEffect(() => {
    const fetchRubrics = async () => {
      try {
        // Fetch question level rubrics
        const questionResponse = await fetch(getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRICS) + '?valid_only=true&rubric_type=question')
        const questionData = await questionResponse.json()
        if (questionData.success) {
          setRubrics(questionData.data)
        }
        
        // Fetch paper level rubrics
        const paperResponse = await fetch(getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRICS) + '?valid_only=true&rubric_type=paper')
        const paperData = await paperResponse.json()
        if (paperData.success) {
          setPaperRubrics(paperData.data)
        }
      } catch (err) {
        console.error('Error fetching rubrics:', err)
      }
    }
    fetchRubrics()
  }, [])

  // Set default paper rubric when paper rubrics are loaded
  useEffect(() => {
    if (paperRubrics.length > 0 && !formData.paperRubricName) {
      // Find the first rubric or the generic one
      const defaultRubric = paperRubrics.find(rubric => rubric.is_generic) || paperRubrics[0]
      if (defaultRubric) {
        setFormData(prev => ({
          ...prev,
          paperRubricName: defaultRubric.name
        }))
      }
    }
  }, [paperRubrics, formData.paperRubricName])

  

  const loadProfessorEmails = async () => {
    try {
      const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_PROFESSOR_EMAILS);
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch professor emails");

      const data = await response.json();
      setProfessorEmails(data.emails || []);
    } catch (error) {
      console.error("Error loading professor emails:", error);
      setError("Failed to load professor emails");
    }
  };

  const loadAssignedCourses = async () => {
    try {
      const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_ASSIGNED_COURSES);
      const params = new URLSearchParams({ professor_email: selectedEmail });
      const response = await fetch(`${url}?${params}`);
      if (!response.ok) throw new Error("Failed to fetch assigned courses");

      const data = await response.json();
      setAssignedCourses(data.courses || []);
    } catch (error) {
      console.error("Error loading assigned courses:", error);
      // Don't set error here as it's not critical
    }
  };

  // Form handling (copied from Analysis page)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset selected paper and analysis when filters change
    // This allows users to see filtered results without losing the paper list
    setSelectedPaper("");
    setAnalysisResults(null);
  };

  const validateForm = () => {
    if (!selectedEmail) {
      setError("Please select a professor email");
      return false;
    }
    return true;
  };

  // Question paper fetching based on email and filters
  const fetchQuestionPapers = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        professor_email: selectedEmail,
      });

      // Add optional filters
      if (formData.academicYear) params.append('academic_year', formData.academicYear);
      if (formData.semester) params.append('semester', formData.semester);
      if (formData.courseCode) params.append('course_code', formData.courseCode);
      if (formData.reviewStatus) params.append('review_status', formData.reviewStatus);
      if (formData.examType) params.append("exam_type", formData.examType);
      if (formData.examFormat) params.append("exam_format", formData.examFormat);
      if (formData.uploaderName) params.append("uploader_name", formData.uploaderName);

      // Add custom field filters
      Object.keys(customFieldFilters).forEach(fieldName => {
        const value = customFieldFilters[fieldName];
        if (value) {
          params.append(`custom_field_${fieldName}`, value);
        }
      });

      const response = await fetch(
        getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_QUESTION_PAPERS) + `?${params}`
      );
      const result = await response.json();

      if (result.success) {
        setQuestionPapers(result.data);
        if (result.data.length === 0) {
          setError("No question papers found for the selected criteria. Make sure you have been assigned papers for review.");
        }
      } else {
        setError("Failed to fetch question papers");
      }
    } catch (error) {
      console.error("Error fetching question papers:", error);
      setError("Failed to fetch question papers. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Check if handout exists for the given course criteria
  const checkHandoutExists = async (courseId, courseTitle, academicYear, semester) => {
    try {
      const params = new URLSearchParams()
      if (courseId) params.append('course_id', courseId)
      if (courseTitle) params.append('course_title', courseTitle)
      params.append('academic_year', academicYear)
      params.append('semester', semester)
      
      const response = await fetch(`/postgres/handouts/check?${params}`)
      const result = await response.json()
      return result.exists || false
    } catch (error) {
      console.error('Error checking handout:', error)
      return false
    }
  }

  // Analysis handling (same as Analysis page, but called "Review Paper")
  const handleReviewPaper = async () => {
    if (!selectedPaper) {
      setError("Please select a question paper to review");
      return;
    }

    // Check if handout exists for this course
    setHandoutWarning('')
    const selectedPaperData = questionPapers.find(p => p.id === selectedPaper);
    if (selectedPaperData) {
      const handoutExists = await checkHandoutExists(
        selectedPaperData.course_id, 
        selectedPaperData.course_name, 
        selectedPaperData.academic_year, 
        selectedPaperData.semester
      )
      if (!handoutExists) {
        setHandoutWarning('⚠️ No course handout found for this course. Paper-level analysis may be less accurate without handout context.')
      }
    }

    setIsAnalyzing(true);
    setError("");
    setAnalysisResults(null);
    setQuestionPdfUrls({});

    try {
      // First, create or get paper review record
      const paperReviewResponse = await fetch(
        getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_PAPER_REVIEW),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question_paper_id: selectedPaper,
            reviewer_email: selectedEmail,
          }),
        }
      );

      if (!paperReviewResponse.ok)
        throw new Error("Failed to create paper review");
      const paperReviewData = await paperReviewResponse.json();
      setPaperReview(paperReviewData);

      // Get existing paper review with questions if it exists
      const existingReviewUrl = `${getApiUrl(
        SERVERS.DATABASE,
        ENDPOINTS.REVIEW_PAPER_REVIEW
      )}/${selectedPaper}?reviewer_email=${selectedEmail}`;
      const existingReviewResponse = await fetch(existingReviewUrl);
      if (existingReviewResponse.ok) {
        const existingData = await existingReviewResponse.json();
        if (existingData.paper_review) {
          setPaperLevelFeedback(
            existingData.paper_review.paper_level_feedback || ""
          );

          // Load existing question reviews
          const existingQuestionReviews = {};
          existingData.paper_review.question_reviews?.forEach((qr) => {
            existingQuestionReviews[qr.question_id] = {
              agreement: qr.reviewer_agreement,
              feedback: qr.reviewer_feedback || "",
            };
          });
          setQuestionReviews(existingQuestionReviews);
        }
      }

      // Use the same analysis endpoint as Analysis page
      const response = await fetch(`/analysis/analyze-paper-concurrent/${selectedPaper}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question_rubric_name: formData.rubricName,
          paper_rubric_name: formData.paperRubricName
        })
      })

      const result = await response.json();

      if (result.success) {
        console.log("Analysis results received:", result.data);
        console.log("Questions analysis:", result.data.questions_analysis);
        console.log("Cache info:", result.cache_info);

        setAnalysisResults(result.data);
        setCacheInfo(result.cache_info);

        // Preload first few question PDFs (same as Analysis page)
        if (
          result.data.questions_analysis &&
          result.data.questions_analysis.length > 0
        ) {
          const firstThreeQuestions = result.data.questions_analysis.slice(
            0,
            3
          );
          console.log("First three questions:", firstThreeQuestions);
          firstThreeQuestions.forEach((question, index) => {
            console.log("Question data:", question);
            if (question.question_id) {
              console.log("Loading PDF for question_id:", question.question_id);
              setTimeout(() => {
                loadQuestionPdf(question.question_id);
              }, index * 500);
            } else {
              console.log("No question_id found for question:", question);
            }
          });
        }
      } else {
        setError("Failed to analyze question paper");
      }
    } catch (error) {
      console.error("Error analyzing question paper:", error);
      setError("Failed to analyze question paper. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // PDF loading (same as Analysis page)
  const loadQuestionPdf = async (questionId, forceRefresh = false) => {
    if (questionPdfUrls[questionId] && !forceRefresh) {
      return; // Already loaded
    }

    if (loadingPdfs[questionId]) {
      return; // Already loading
    }

    setLoadingPdfs((prev) => ({ ...prev, [questionId]: true }));
    setFailedPdfs((prev) => ({ ...prev, [questionId]: false }));

    try {
      console.log('Loading PDF for question ID:', questionId)
      // Add cache-busting parameter to force refresh
      const cacheBuster = forceRefresh ? `?t=${Date.now()}` : ''
      const response = await fetch(`/analysis/question-pdf/${questionId}${cacheBuster}`)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      
      // If refreshing, clean up old URL
      if (forceRefresh && questionPdfUrls[questionId]) {
        URL.revokeObjectURL(questionPdfUrls[questionId])
      }
      
      setQuestionPdfUrls(prev => ({ ...prev, [questionId]: url }))
      console.log('Successfully loaded PDF for question:', questionId)
    } catch (error) {
      console.error("Error loading question PDF:", error);
      setFailedPdfs((prev) => ({ ...prev, [questionId]: true }));
    } finally {
      setLoadingPdfs((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  // Question expansion toggle (same as Analysis page)
  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  // Review-specific functions
  const handleQuestionReview = useCallback(
    async (questionId, questionNumber, agreement, feedback = "") => {
      // Set saving state for this question
      setSavingQuestionFeedback((prev) => ({ ...prev, [questionId]: true }));
      
      try {
        // Get current feedback if disagreeing and no new feedback provided
        let currentFeedback = feedback;
        if (!agreement && !feedback) {
          const existingReview = questionReviews[questionId];
          if (existingReview) {
            currentFeedback = existingReview.feedback || "";
          }
        }

        const response = await fetch(
          getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_QUESTION_REVIEW),
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              paper_review_id: paperReview.id,
              question_id: questionId,
              question_number: questionNumber,
              reviewer_agreement: agreement,
              reviewer_feedback: currentFeedback || null,
            }),
          }
        );

        if (!response.ok) throw new Error("Failed to save question review");

        const updatedReview = await response.json();

        // Update local state with response data
        setQuestionReviews((prev) => ({
          ...prev,
          [questionId]: {
            agreement: updatedReview.reviewer_agreement,
            feedback: updatedReview.reviewer_feedback || "",
          },
        }));
      } catch (error) {
        console.error("Error saving question review:", error);
        setError("Failed to save question review");
      } finally {
        // Clear saving state for this question
        setSavingQuestionFeedback((prev) => {
          const newState = { ...prev };
          delete newState[questionId];
          return newState;
        });
      }
    },
    [paperReview?.id, questionReviews, setQuestionReviews, setError]
  );

  // Debounced save function for question-level feedback (increased to 1500ms)
  const debouncedSave = useCallback(
    (questionId, questionNumber, feedback) => {
      if (savingFeedbackTimeout.current) {
        clearTimeout(savingFeedbackTimeout.current);
      }
      savingFeedbackTimeout.current = setTimeout(() => {
        handleQuestionReview(questionId, questionNumber, false, feedback);
      }, 1500); // Wait 1.5 seconds after last change before saving
    },
    [handleQuestionReview]
  );

  // Save immediately on blur (when user clicks away)
  const handleQuestionFeedbackBlur = useCallback(
    (questionId, questionNumber, feedback) => {
      // Clear any pending debounced save
      if (savingFeedbackTimeout.current) {
        clearTimeout(savingFeedbackTimeout.current);
        savingFeedbackTimeout.current = null;
      }
      // Save immediately
      if (feedback && feedback.trim() !== "") {
        handleQuestionReview(questionId, questionNumber, false, feedback);
      }
    },
    [handleQuestionReview]
  );

  // Debounced save function for paper-level feedback
  const debouncedSavePaperFeedback = useCallback(async (feedback) => {
    if (!paperReview?.id) return;
    
    // Clear any pending save
    if (savingPaperFeedbackTimeout.current) {
      clearTimeout(savingPaperFeedbackTimeout.current);
    }
    
    // Set saving state
    setSavingPaperFeedback(true);
    
    try {
      const response = await fetch(
        getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_PAPER_FEEDBACK),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paper_review_id: paperReview.id,
            paper_level_feedback: feedback,
            review_status: paperReview.review_status, // Keep existing status
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to auto-save paper feedback");
      
      // Optionally update paper review state if needed
      const result = await response.json();
      if (result.paper_level_feedback !== undefined) {
        setPaperLevelFeedback(result.paper_level_feedback || feedback);
      }
    } catch (error) {
      console.error("Error auto-saving paper feedback:", error);
      // Don't show error for auto-save failures, just log
    } finally {
      setSavingPaperFeedback(false);
    }
  }, [paperReview?.id]);

  const handleSavePaperFeedback = async () => {
    if (!paperReview?.id) return;

    // Validate that all disagreed questions have feedback
    const missingFeedback = [];
    analysisResults.questions_analysis.forEach((question) => {
      const review = questionReviews[question.question_id];
      if (
        review &&
        review.agreement === false &&
        (!review.feedback || review.feedback.trim() === "")
      ) {
        missingFeedback.push(question.question_number);
      }
    });

    if (missingFeedback.length > 0) {
      setError(
        `Please provide feedback for disagreed questions: ${missingFeedback.join(
          ", "
        )}`
      );
      return;
    }

    setSavingReview(true);
    setError(""); // Clear any previous errors

    try {
      const response = await fetch(
        getApiUrl(SERVERS.DATABASE, ENDPOINTS.REVIEW_PAPER_FEEDBACK),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paper_review_id: paperReview.id,
            paper_level_feedback: paperLevelFeedback,
            review_status: "completed",
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to save paper feedback");

      alert("Paper review saved successfully!");
    } catch (error) {
      console.error("Error saving paper feedback:", error);
      setError("Failed to save paper feedback");
    } finally {
      setSavingReview(false);
    }
  };

  const getReportFileBaseName = () => {
    const paperName =
      analysisResults?.original_filename ||
      analysisResults?.paper_metadata?.original_filename ||
      `paper_${selectedPaper || "report"}`;
    return `${paperName}`.replace(/[^a-zA-Z0-9._-]/g, "_");
  };

  const prepareReportView = async () => {
    if (!analysisResults?.questions_analysis?.length) return () => {};

    const previousExpanded = expandedQuestions;
    const expandedMap = {};
    analysisResults.questions_analysis.forEach((q) => {
      expandedMap[q.question_id] = true;
    });

    setExpandedQuestions(expandedMap);
    await new Promise((resolve) => setTimeout(resolve, 400));

    const pdfViewerElements = Array.from(
      reportContainerRef.current?.querySelectorAll(".question-pdf-viewer") || []
    );
    pdfViewerElements.forEach((element) => {
      element.dataset.prevDisplay = element.style.display || "";
      element.style.display = "none";
    });

    const missingPdfIds = analysisResults.questions_analysis
      .map((q) => q.question_id)
      .filter((id) => id && !questionPdfUrls[id]);

    if (missingPdfIds.length > 0) {
      await Promise.all(
        missingPdfIds.map(async (id) => {
          try {
            await loadQuestionPdf(id);
          } catch {
            // continue even if one PDF fails
          }
        })
      );
      await new Promise((resolve) => setTimeout(resolve, 400));
    }

    return () => {
      setExpandedQuestions(previousExpanded);
      pdfViewerElements.forEach((element) => {
        element.style.display = element.dataset.prevDisplay || "";
        delete element.dataset.prevDisplay;
      });
    };
  };

  const handleDownloadReportPdf = async () => {
    if (!analysisResults || !reportContainerRef.current) {
      setError("Please analyze a paper before downloading the report.");
      return;
    }

    setDownloadingReport(true);
    setError("");

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - margin * 2;
      let currentY = margin;
      const LINE_HEIGHT = 5.6;
      const SECTION_GAP = 2.2;
      const colors = {
        primary: [30, 58, 138],
        accent: [59, 130, 246],
        text: [31, 41, 55],
        muted: [107, 114, 128],
        panel: [239, 246, 255],
      };

      const drawPageHeader = () => {
        pdf.setFillColor(...colors.primary);
        pdf.rect(0, 0, pageWidth, 10, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.text("Question Paper Review Report", margin, 6.7);
        pdf.setTextColor(...colors.text);
      };

      const drawMultilineText = (lines, x, y, lineHeight = LINE_HEIGHT) => {
        lines.forEach((line, index) => {
          pdf.text(line, x, y + index * lineHeight);
        });
      };

      const wrapParagraphs = (text, width) => {
        const normalized = String(text ?? "N/A").replace(/\r\n/g, "\n");
        const paragraphs = normalized.split("\n");
        const wrappedLines = [];

        paragraphs.forEach((paragraph, index) => {
          const safeParagraph = paragraph.trim() === "" ? " " : paragraph;
          const lines = pdf.splitTextToSize(safeParagraph, width);
          wrappedLines.push(...(lines.length > 0 ? lines : [" "]));
          if (index < paragraphs.length - 1) {
            wrappedLines.push(" ");
          }
        });

        return wrappedLines;
      };

      const ensureSpace = (requiredHeight = 8) => {
        if (currentY + requiredHeight > pageHeight - margin) {
          pdf.addPage();
          drawPageHeader();
          currentY = margin + 2;
        }
      };

      const addSection = (text) => {
        ensureSpace(14);
        pdf.setFillColor(...colors.panel);
        pdf.roundedRect(margin, currentY - 1.7, contentWidth, 9.5, 1.6, 1.6, "F");
        pdf.setDrawColor(...colors.accent);
        pdf.setLineWidth(0.6);
        pdf.line(margin, currentY + 7.4, margin + contentWidth, currentY + 7.4);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...colors.primary);
        pdf.setFontSize(12);
        pdf.text(text, margin + 2, currentY + 4.2);
        pdf.setTextColor(...colors.text);
        currentY += 12;
      };

      const addHeading = (text, size = 14) => {
        ensureSpace(size === 14 ? 10 : 8);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...colors.primary);
        pdf.setFontSize(size);
        pdf.text(text, margin, currentY);
        pdf.setTextColor(...colors.text);
        currentY += size === 14 ? 8 : 7;
      };

      const addLabelValue = (label, value) => {
        const labelText = `${label}:`;
        const wrappedLabel = wrapParagraphs(labelText, contentWidth);
        const wrappedValue = wrapParagraphs(value ?? "N/A", contentWidth - 2);
        const labelHeight = wrappedLabel.length * LINE_HEIGHT;
        const valueHeight = wrappedValue.length * LINE_HEIGHT;
        const blockHeight = labelHeight + valueHeight + 1.2;
        ensureSpace(blockHeight + 0.8);

        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...colors.text);
        pdf.setFontSize(10);
        drawMultilineText(wrappedLabel, margin, currentY);

        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...colors.muted);
        drawMultilineText(wrappedValue, margin + 2, currentY + labelHeight + 0.8);

        pdf.setTextColor(...colors.text);
        currentY += blockHeight + 1.2;
      };

      const addParagraph = (text = "") => {
        const wrapped = wrapParagraphs(text || "N/A", contentWidth);
        const lineCount = Math.max(wrapped.length, 1);
        const blockHeight = lineCount * LINE_HEIGHT;
        ensureSpace(blockHeight + 0.8);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...colors.text);
        pdf.setFontSize(10);
        drawMultilineText(wrapped, margin, currentY);
        currentY += blockHeight + 0.8;
      };

      const addList = (title, items) => {
        addHeading(title, 11);
        if (!Array.isArray(items) || items.length === 0) {
          addParagraph("• None");
          return;
        }
        items.forEach((item) => addParagraph(`• ${item}`));
      };

      drawPageHeader();
      currentY = margin + 2;

      const metadata = analysisResults.paper_metadata || {};
      const metrics = analysisResults.overall_metrics || {};
      const timeAnalysis = metrics.time_analysis || {};

      addHeading("Question Paper Review Report", 16);
      addLabelValue("Generated On", new Date().toLocaleString());
      addLabelValue("File", analysisResults.original_filename || metadata.original_filename || "N/A");
      addLabelValue("Course", analysisResults.course_name || metadata.course_name || "N/A");
      addLabelValue("Instructor", analysisResults.instructor_name || metadata.instructor_name || "N/A");
      addLabelValue("Academic Year", metadata.academic_year || "N/A");
      addLabelValue("Semester", metadata.semester || "N/A");

      addSection("Overall Metrics");
      addLabelValue("Average Quality Score", `${(metrics.average_quality_score || 0).toFixed(2)}/10`);
      addLabelValue("Weighted Difficulty", `${(metrics.weighted_difficulty || 0).toFixed(1)}/6`);
      addLabelValue("Time Efficiency", `${timeAnalysis.time_efficiency_percentage || 0}%`);
      addLabelValue("Time Status", timeAnalysis.time_status || "N/A");
      addLabelValue("Total Questions", metrics.total_questions || analysisResults.questions_analysis?.length || 0);
      addLabelValue("Total Marks", metrics.total_marks || analysisResults.total_marks || 0);

      if (metrics.blooms_distribution) {
        addSection("Bloom's Taxonomy Distribution");
        Object.entries(metrics.blooms_distribution).forEach(([level, count]) => {
          addLabelValue(`Level ${level}`, count);
        });
      }

      if (Array.isArray(analysisResults.recommendations)) {
        addSection("Recommendations");
        addList("Recommendations", analysisResults.recommendations);
      }

      currentY += SECTION_GAP;

      if (analysisResults.score_breakdown) {
        addSection("Final Evaluation");
        addLabelValue("Final Weighted Score", analysisResults.final_weighted_score?.toFixed?.(1) || "0.0");
        addLabelValue("Final Rating", analysisResults.final_rating || "N/A");
        addLabelValue("Recommendation", analysisResults.final_recommendation || "N/A");

        const questionLevel = analysisResults.score_breakdown.question_level || {};
        const paperLevel = analysisResults.score_breakdown.paper_level || {};
        addLabelValue("Question Level Raw Score", questionLevel.raw_score);
        addLabelValue("Question Level Weighted Score", questionLevel.weighted_score);
        addLabelValue("Paper Level Raw Score", paperLevel.raw_score);
        addLabelValue("Paper Level Weighted Score", paperLevel.weighted_score);
        addLabelValue("Incomplete Questions", analysisResults.score_breakdown.incomplete_questions || 0);
      }

      if (analysisResults.paper_level_analysis?.rubric_assessment) {
        addSection("Paper Level Analysis");
        Object.entries(analysisResults.paper_level_analysis.rubric_assessment).forEach(([criterion, assessment]) => {
          addHeading(criterion, 11);
          addLabelValue("Score", assessment?.score || "N/A");
          addLabelValue("Justification", assessment?.justification || "N/A");
        });
      }

      addSection("Question-wise Analysis");
      const questions = analysisResults.questions_analysis || [];
      questions.forEach((question, idx) => {
        pdf.addPage();
        drawPageHeader();
        currentY = margin + 2;

        const questionReview = questionReviews[question.question_id] || {};
        const reviewerAgreement =
          questionReview.agreement === true
            ? "Agree"
            : questionReview.agreement === false
            ? "Disagree"
            : "Not reviewed";

        addHeading(`Question ${question.question_number || idx + 1}`, 14);
        addLabelValue("Question ID", question.question_id || "N/A");
        addLabelValue("Marks", question.marks || "N/A");
        addLabelValue("Quality Score", `${(question.quality_score || 0).toFixed(1)}/10`);
        addLabelValue("Bloom's Level", question.blooms_level || "N/A");
        addLabelValue("Estimated Time", `${question.estimated_time_minutes || 30} minutes`);
        addLabelValue("Reviewer Decision", reviewerAgreement);
        addLabelValue("Reviewer Feedback", questionReview.feedback || "N/A");

        addHeading("Question Text", 11);
        addParagraph(question.question_text || "Not available");

        if (question.rubric_assessment) {
          addHeading("Rubric Assessment", 11);
          Object.entries(question.rubric_assessment).forEach(([key, value]) => {
            if (key.endsWith("_justification")) return;

            let score = "N/A";
            let justification = "No justification available";

            if (value && typeof value === "object") {
              score = value.score || "N/A";
              justification = value.justification || "No justification available";
            } else if (typeof value === "string" || typeof value === "number") {
              score = String(value);
            }

            addLabelValue(key, score);
            addLabelValue(`${key} Justification`, justification);
          });
        }

        addList("Strengths", question.strengths || []);
        addList("Areas for Improvement", question.weaknesses || []);
      });

      pdf.addPage();
      drawPageHeader();
      currentY = margin + 2;
      addHeading("Overall Paper Review", 14);
      addParagraph(paperLevelFeedback || "No overall paper feedback provided.");

      const totalPages = pdf.getNumberOfPages();
      for (let page = 1; page <= totalPages; page++) {
        pdf.setPage(page);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(...colors.muted);
        pdf.text(
          `Page ${page} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 5,
          { align: "right" }
        );
      }

      pdf.save(`${getReportFileBaseName()}_review_report.pdf`);
    } catch (err) {
      console.error("Error generating PDF report:", err);
      setError("Failed to generate PDF report. Please try again.");
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDownloadReportDoc = async () => {
    if (!analysisResults || !reportContainerRef.current) {
      setError("Please analyze a paper before downloading the report.");
      return;
    }

    setDownloadingReport(true);
    setError("");

    let restoreView = () => {};
    try {
      restoreView = await prepareReportView();
      const reportHtml = reportContainerRef.current.innerHTML;
      const title = analysisResults?.original_filename || "Review Report";

      const htmlDocument = `
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${title}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; color: #111; }
              h1, h2, h3, h4, h5, h6 { margin-top: 16px; }
              table { border-collapse: collapse; width: 100%; }
              td, th { border: 1px solid #ccc; padding: 6px; }
              img { max-width: 100%; height: auto; }
            </style>
          </head>
          <body>
            <h1>Question Paper Review Report</h1>
            <p>Generated on: ${new Date().toLocaleString()}</p>
            ${reportHtml}
          </body>
        </html>
      `;

      const blob = new Blob([htmlDocument], {
        type: "application/msword;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${getReportFileBaseName()}_review_report.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generating DOC report:", err);
      setError("Failed to generate DOC report. Please try again.");
    } finally {
      restoreView();
      setDownloadingReport(false);
    }
  };

  return (
    <div className="analyze-container">
      <div className="container">
        {/* Header */}
        <div className="analyze-header">
          <div className="header-icon">
            <FontAwesomeIcon icon={faClipboardCheck} style={{ marginRight: "6px" }}/>
          </div>
          <div className="header-text">
            <h1>Review Papers</h1>
            <p>
              Review and provide feedback on question papers assigned through
              the reviewer mapping system.
            </p>
          </div>
        </div>

       

        {/* Filters Section */}
        <div className="form-section">
          <div 
            className="section-header" 
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <h2>
              <FontAwesomeIcon icon={faSearch} className="section-icon" style={{ marginRight: "6px" }}/>
              Filters (Optional)
              <FontAwesomeIcon 
                icon={filtersExpanded ? faChevronUp : faChevronDown} 
                style={{ marginLeft: "10px", fontSize: "0.9em" }}
              />
            </h2>
            <p>Use these filters to narrow down your assigned question papers</p>
          </div>

          {filtersExpanded && (
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="academicYear">
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "6px" }}/>
                Academic Year
              </label>
              <select
                id="academicYear"
                name="academicYear"
                value={formData.academicYear}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">All Academic Years</option>
                {academicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="semester">
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "6px" }}/>
                Semester
              </label>
              <select
                id="semester"
                name="semester"
                value={formData.semester}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">All Semesters</option>
                <option value="1">Semester 1</option>
                <option value="2">Semester 2</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="courseCode">
                <FontAwesomeIcon icon={faBookOpen} style={{ marginRight: "6px" }}/>
                Course
              </label>
              <select
                id="courseCode"
                name="courseCode"
                value={formData.courseCode}
                onChange={handleInputChange}
                className="form-control"
                disabled={!selectedEmail}
              >
                <option value="">All Assigned Courses</option>
                {assignedCourses.map((course) => (
                  <option key={course.course_code} value={course.course_code}>
                    {course.course_code} - {course.course_title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="reviewStatus">
                <FontAwesomeIcon icon={faClipboardCheck} style={{ marginRight: "6px" }}/>
                Review Status
              </label>
              <select
                id="reviewStatus"
                name="reviewStatus"
                value={formData.reviewStatus}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">All Papers</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Done</option>
              </select>
            </div>

            <div className="form-group">
              <label>Exam Type</label>
              <select
                name="examType"
                value={formData.examType}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">All Exam Types</option>
                {examTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Exam Format</label>
              <select
                name="examFormat"
                value={formData.examFormat}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">All Formats</option>
                {examFormats.map(format => (
                  <option key={format} value={format}>{format}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Uploader Name</label>
              <select
                name="uploaderName"
                value={formData.uploaderName}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">All Uploaders</option>
                {uploaders.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            {/* Dynamic Custom Field Filters - Only show select fields with options */}
            {customFields
              .filter(field => field.field_type === 'select' && field.options && field.options.length > 0)
              .map((field) => (
              <div key={field.field_name} className="form-group">
                <label>{field.field_label}</label>
                <select
                  name={field.field_name}
                  value={customFieldFilters[field.field_name] || ''}
                  onChange={(e) => {
                    setCustomFieldFilters(prev => ({
                      ...prev,
                      [field.field_name]: e.target.value
                    }));
                    // Reset selected paper and analysis when filters change
                    setSelectedPaper("");
                    setAnalysisResults(null);
                  }}
                  className="form-control"
                >
                  <option value="">All {field.field_label}</option>
                  {field.options.map((option, idx) => (
                    <option key={idx} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            ))}

          </div>
          )}
        </div>

        {/* Rubrics Section */}
        <div className="form-section">
          <div 
            className="section-header"
            onClick={() => setRubricsExpanded(!rubricsExpanded)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <h2>
              <FontAwesomeIcon icon={faClipboardCheck} className="section-icon" style={{ marginRight: "6px" }}/>
              Assessment Rubrics
              <FontAwesomeIcon 
                icon={rubricsExpanded ? faChevronUp : faChevronDown} 
                style={{ marginLeft: "10px", fontSize: "0.9em" }}
              />
            </h2>
            <p>Select the rubrics to use for question and paper level assessment</p>
          </div>

          {rubricsExpanded && (
          <>
          <div className="form-grid">

            <div className="form-group">
              <label htmlFor="rubricName">
                <FontAwesomeIcon icon={faClipboardCheck} style={{ marginRight: "6px" }}/>
                Question Level Assessment Rubric *
              </label>
              <select
                id="rubricName"
                name="rubricName"
                value={formData.rubricName}
                onChange={handleInputChange}
                className="form-control"
                required
              >
                {rubrics.map(rubric => (
                  <option key={rubric.id} value={rubric.name}>
                    {rubric.name} {rubric.is_generic ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="paperRubricName">
                <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: "6px" }} />
                Paper Level Assessment Rubric
              </label>
              <select
                id="paperRubricName"
                name="paperRubricName"
                value={formData.paperRubricName}
                onChange={handleInputChange}
                className="form-control"
              >
                {paperRubrics.map(rubric => (
                  <option key={rubric.id} value={rubric.name}>
                    {rubric.name} {rubric.is_generic ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isLoading && (
            <div className="loading-indicator">
              <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
              <span>Fetching papers...</span>
            </div>
          )}
          </>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-card">
            <div className="error-content">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="error-icon"
                style={{ marginRight: "6px" }}
              />
              <div className="error-text">
                <h3>Error</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Question Papers Selection (same as Analysis page) */}
        {questionPapers.length > 0 && (
          <div className="papers-section">
            <h2>
              <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: "6px" }}/>
              Available Question Papers
            </h2>

            <div className="papers-grid">
              {questionPapers.map((paper) => (
                <div
                  key={paper.id}
                  className={`paper-card ${
                    selectedPaper === paper.id ? "selected" : ""
                  }`}
                  onClick={() => setSelectedPaper(paper.id)}
                >
                  <div
                    className="paper-header"
                    title={paper.filename || paper.original_filename}
                  >
                    <FontAwesomeIcon icon={faFilePdf} className="paper-icon" style={{ marginRight: "6px" }}/>
                    <h3>{paper.filename || paper.original_filename}</h3>
                  </div>

                  <div className="paper-details">
                    <p>
                      <strong>Course:</strong>{" "}
                      {paper.course_name || paper.course_id}
                    </p>
                    <p>
                      <strong>Instructor:</strong>{" "}
                      {paper.instructor_name || "Not specified"}
                    </p>
                    <p>
                      <strong>Academic Year:</strong> {paper.academic_year}
                    </p>
                    <p>
                      <strong>Semester:</strong> {paper.semester}
                    </p>
                    {paper.program_category && (
                      <p>
                        <strong>Program Category:</strong> {paper.program_category}
                      </p>
                    )}
                    {paper.program_committee && (
                      <p>
                        <strong>Program Committee:</strong> {paper.program_committee}
                      </p>
                    )}
                    <p>
                      <strong>Questions:</strong> {paper.question_count}
                    </p>
                    {paper.review_progress && (
                      <p>
                        <strong>Review Progress:</strong>{" "}
                        <span className={`review-progress ${paper.review_progress.status}`}>
                          {paper.review_progress.completed_questions}/{paper.review_progress.total_questions} questions reviewed
                          {paper.review_progress.status === 'completed' && (
                            <FontAwesomeIcon icon={faCheckCircle} className="status-icon completed" style={{ marginRight: "6px" }}/>
                          )}
                          {paper.review_progress.status === 'in_progress' && (
                            <FontAwesomeIcon icon={faSpinner} className="status-icon in-progress" style={{ marginRight: "6px" }}/>
                          )}
                        </span>
                      </p>
                    )}
                    <p>
                      <strong>Total Marks:</strong> {paper.total_marks}
                    </p>
                    <p>
                      <strong>Duration:</strong>{" "}
                      {formatTime(paper.total_duration_minutes || 180)}
                    </p>
                  </div>

                  {selectedPaper === paper.id && (
                    <div className="selection-indicator">
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "6px" }}/>
                      Selected
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Handout Warning */}
            {handoutWarning && (
              <div className="handout-warning">
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: "6px" }}/>
                <span>{handoutWarning}</span>
              </div>
            )}

            <div className="analyze-action">
              <button
                className="btn btn-analyze"
                onClick={handleReviewPaper}
                disabled={!selectedPaper || isAnalyzing}
              >
                {isAnalyzing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faClipboardCheck} style={{ marginRight: "6px" }}/>
                    Review Question Paper
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Analysis Results with Review Interface */}
        {isAnalyzing && (
          <div className="analysis-loading">
            <div className="loading-content">
              <FontAwesomeIcon
                icon={faSpinner}
                className="spinning large-spinner"
                style={{ marginRight: "6px" }}
              />
              <h3>Analyzing Question Paper</h3>
              <p>Please wait while we analyze the question paper...</p>
            </div>
          </div>
        )}

        {analysisResults && (
          <div className="analysis-results" ref={reportContainerRef}>
            {/* Results Header - Exact copy from Analysis page */}
            <div className="results-header">
              <h2>
                <FontAwesomeIcon icon={faChartBar} style={{ marginRight: "6px" }}/>
                Analysis Results
              </h2>
              <div className="paper-info">
                <h3>
                  {analysisResults.original_filename ||
                    analysisResults.paper_metadata?.original_filename}
                </h3>
                <p>
                  {analysisResults.course_name ||
                    analysisResults.paper_metadata?.course_name}{" "}
                  •{" "}
                  {analysisResults.instructor_name ||
                    analysisResults.paper_metadata?.instructor_name}
                </p>
                {cacheInfo && (
                  <div
                    className={`cache-indicator ${
                      cacheInfo.source === "database_cache" ? "cached" : "live"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={
                        cacheInfo.source === "database_cache"
                          ? faDownload
                          : faBrain
                      }
                      style={{ marginRight: "6px" }}
                    />
                    {cacheInfo.source === "database_cache"
                      ? "Ultra-fast analysis result retrieved from cache"
                      : "Fresh analysis"}
                  </div>
                )}
              </div>
            </div>

            {/* Overall Metrics - Exact copy from Analysis page */}
            <div className="overall-metrics">
              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faStar} style={{ marginRight: "6px" }}/>
                </div>
                <div className="metric-content">
                  <h4>Overall Quality</h4>
                  <div
                    className="metric-value"
                    style={{
                      color: getQualityScoreColor(
                        analysisResults.overall_metrics
                          ?.average_quality_score || 0
                      ),
                    }}
                  >
                    {(
                      analysisResults.overall_metrics?.average_quality_score ||
                      0
                    ).toFixed(2)}
                    /10
                  </div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faGraduationCap} style={{ marginRight: "6px" }}/>
                </div>
                <div className="metric-content">
                  <h4>Difficulty Level</h4>
                  <div className="metric-value">
                    {(
                      analysisResults.overall_metrics?.weighted_difficulty || 0
                    ).toFixed(1)}
                    /6
                  </div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faClock} style={{ marginRight: "6px" }}/>
                </div>
                <div className="metric-content">
                  <h4>
                    Time Allocation Ratio{" "}
                    <FontAwesomeIcon 
                      icon={faInfoCircle} 
                      style={{ 
                        fontSize: '0.8em', 
                        color: '#6c757d', 
                        cursor: 'help' 
                      }}
                      title="Ratio of allocated exam time to estimated completion time. Values >100% indicate sufficient time allocation."
                    />
                  </h4>
                  <div
                    className="metric-value"
                    style={{
                      color: getTimeEfficiencyColor(
                        analysisResults.overall_metrics?.time_analysis
                          ?.time_status || "Normal"
                      ),
                    }}
                  >
                    {analysisResults.overall_metrics?.time_analysis
                      ?.time_efficiency_percentage || 0}
                    %
                  </div>
                  <div className="metric-subtitle">
                    {analysisResults.overall_metrics?.time_analysis
                      ?.time_status || "Normal"}
                  </div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faBullseye} style={{ marginRight: "6px" }}/>
                </div>
                <div className="metric-content">
                  <h4>Questions</h4>
                  <div className="metric-value">
                    {analysisResults.overall_metrics?.total_questions ||
                      analysisResults.questions_analysis?.length ||
                      0}
                  </div>
                  <div className="metric-subtitle">
                    {analysisResults.overall_metrics?.total_marks ||
                      analysisResults.total_marks ||
                      0}{" "}
                    total marks
                  </div>
                </div>
              </div>
            </div>

            {/* Bloom's Taxonomy Distribution - Exact copy from Analysis page */}
            {analysisResults.overall_metrics?.blooms_distribution && (
              <div className="blooms-distribution">
                <h3>Bloom's Taxonomy Distribution</h3>
                <div className="blooms-chart">
                  {Object.entries(
                    analysisResults.overall_metrics.blooms_distribution
                  ).map(([level, count]) => {
                    const levelNames = [
                      "",
                      "Remember",
                      "Understand",
                      "Apply",
                      "Analyze",
                      "Evaluate",
                      "Create",
                    ];
                    const levelName = levelNames[parseInt(level)] || "Unknown";
                    return (
                      <div key={level} className="bloom-bar">
                        <div className="bloom-label">
                          {level}: {levelName}
                        </div>
                        <div className="bloom-bar-container">
                          <div
                            className="bloom-bar-fill"
                            style={{
                              width: `${
                                (count /
                                  (analysisResults.overall_metrics
                                    ?.total_questions ||
                                    analysisResults.questions_analysis
                                      ?.length ||
                                    1)) *
                                100
                              }%`,
                            }}
                          >
                            {count}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Recommendations - same as Analysis page */}
            {analysisResults.recommendations &&
              analysisResults.recommendations.length > 0 && (
                <div className="recommendations">
                  <h3>
                    <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: "6px" }}/>
                    Recommendations
                  </h3>
                  <div className="recommendations-list">
                    {analysisResults.recommendations.map((rec, index) => (
                      <div key={index} className="recommendation-item">
                        <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "6px" }}/>
                        {rec}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Final Evaluation Framework */}
          {analysisResults.score_breakdown && (
            <div className="final-evaluation-framework">
              <h3>
                <FontAwesomeIcon icon={faAward} style={{ marginRight: "6px" }}/>
                Final Evaluation Framework
              </h3>
              <div className="evaluation-card">
                <div className="evaluation-header">
                  <div className="final-score-display">
                    <div className="score-circle">
                      <span className="score-number">{analysisResults.final_weighted_score?.toFixed(1) || '0.0'}</span>
                      <span className="score-label">Final Score</span>
                    </div>
                    <div className="rating-info">
                      <div className="rating-badge">{analysisResults.final_rating || 'N/A'}</div>
                      <div className="recommendation-text">{analysisResults.final_recommendation || 'No recommendation available'}</div>
                    </div>
                  </div>
                </div>
                
                <div className="score-breakdown">
                  <h4>Score Breakdown</h4>
                  <div className="breakdown-grid">
                    <div className="breakdown-item">
                      <div className="breakdown-header">
                        <span className="breakdown-label">Individual Questions</span>
                        <span className="breakdown-weight">({(analysisResults.score_breakdown.question_level.weight * 100).toFixed(0)}%)</span>
                      </div>
                      <div className="breakdown-scores">
                        <span className="raw-score">{analysisResults.score_breakdown.question_level.raw_score}</span>
                        <span className="arrow">→</span>
                        <span className="weighted-score">{analysisResults.score_breakdown.question_level.weighted_score}</span>
                      </div>
                    </div>
                    
                    {analysisResults.score_breakdown.paper_level.weight > 0 && (
                      <div className="breakdown-item">
                        <div className="breakdown-header">
                          <span className="breakdown-label">Overall Paper Criteria</span>
                          <span className="breakdown-weight">({(analysisResults.score_breakdown.paper_level.weight * 100).toFixed(0)}%)</span>
                        </div>
                        <div className="breakdown-scores">
                          <span className="raw-score">{analysisResults.score_breakdown.paper_level.raw_score}</span>
                          <span className="arrow">→</span>
                          <span className="weighted-score">{analysisResults.score_breakdown.paper_level.weighted_score}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="breakdown-summary">
                    <div className="summary-item">
                      <span className="summary-label">Total Questions Analyzed:</span>
                      <span className="summary-value">{analysisResults.score_breakdown.total_questions}</span>
                    </div>
                    {analysisResults.score_breakdown.incomplete_questions > 0 && (
                      <div className="summary-item incomplete">
                        <span className="summary-label">Incomplete Questions:</span>
                        <span className="summary-value">{analysisResults.score_breakdown.incomplete_questions}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Paper Level Analysis */}
            {analysisResults.paper_level_analysis && (
              <div className="paper-level-analysis">
                <h3>
                  <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: "6px" }}/>
                  Paper Level Analysis
                </h3>
                <div className="paper-analysis-card">
                   <div className="paper-analysis-header">
                     <h4>Overall Paper Assessment</h4>
                   </div>
                  
                  <div className="paper-rubric-assessment">
                    <h5>Paper Level Criteria Assessment:</h5>
                    <div className="paper-criteria-grid">
                      {Object.entries(analysisResults.paper_level_analysis.rubric_assessment).map(([criterion, assessment]) => (
                        <div key={criterion} className="paper-criterion-item">
                          <div className="criterion-header">
                            <h6>{criterion}</h6>
                            <span className="criterion-score">{assessment.score}</span>
                          </div>
                          <div className="criterion-justification">
                            <p>{assessment.justification}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Questions Analysis - Exact copy from Analysis page */}
            <div className="questions-analysis">
              <h3>Question-wise Analysis</h3>
              <div className="questions-list">
                {analysisResults.questions_analysis &&
                analysisResults.questions_analysis.length > 0 ? (
                  analysisResults.questions_analysis.map((question, index) => {
                    const isExpanded = expandedQuestions[question.question_id];
                    const review = questionReviews[question.question_id] || {};
                    const isAgreed = review.agreement === true;
                    const isDisagreed = review.agreement === false;

                    return (
                      <div key={question.question_id} className="question-card">
                        <div
                          className="question-header"
                          onClick={() =>
                            toggleQuestionExpansion(question.question_id)
                          }
                        >
                          <div className="question-info">
                            <h4>Question {question.question_number}</h4>
                            <div className="question-metrics">
                              <span className="metric">
                                <FontAwesomeIcon icon={faStar} style={{ marginRight: "6px" }}/>
                                Quality:{" "}
                                {(question.quality_score || 0).toFixed(1)}/10
                              </span>
                              <span className="metric">
                                <FontAwesomeIcon icon={faGraduationCap} style={{ marginRight: "6px" }}/>
                                Bloom: {question.blooms_level || "N/A"} (
                                {question.blooms_level === 1
                                  ? "Remember"
                                  : question.blooms_level === 2
                                  ? "Understand"
                                  : question.blooms_level === 3
                                  ? "Apply"
                                  : question.blooms_level === 4
                                  ? "Analyze"
                                  : question.blooms_level === 5
                                  ? "Evaluate"
                                  : question.blooms_level === 6
                                  ? "Create"
                                  : "Unknown"}
                                )
                              </span>
                              <span className="metric">
                                <FontAwesomeIcon icon={faClock} style={{ marginRight: "6px" }}/>
                                Time:{" "}
                                {formatTime(
                                  question.estimated_time_minutes || 30
                                )}
                              </span>
                              <span className="metric">
                                Marks: {question.marks || "N/A"}
                              </span>
                            </div>
                          </div>
                          <div className="expand-icon">
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronUp : faChevronDown}
                              style={{ marginRight: "6px" }}
                            />
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="question-details">
                            <div className="question-pdf-viewer">
                              <h5>
                                <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: "6px" }}/>
                                Question PDF:
                              </h5>
                              {loadingPdfs[question.question_id] ? (
                                <div className="pdf-loading">
                                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "6px" }}/>
                                  <p>Loading PDF...</p>
                                </div>
                              ) : failedPdfs[question.question_id] ? (
                                <div className="pdf-error">
                                  <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    style={{ marginRight: "6px" }}
                                  />
                                  <div>
                                    Failed to load PDF for Question{" "}
                                    {question.question_number}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.9rem",
                                      marginTop: "0.5rem",
                                    }}
                                  >
                                    Question ID: {question.question_id}
                                  </div>
                                  <div className="pdf-error-actions">
                                    <button
                                      onClick={() =>
                                        loadQuestionPdf(
                                          question.question_id,
                                          true
                                        )
                                      }
                                      className="btn btn-primary btn-sm"
                                      disabled={
                                        loadingPdfs[question.question_id]
                                      }
                                    >
                                      <FontAwesomeIcon
                                        icon={faSyncAlt}
                                        spin={loadingPdfs[question.question_id]}
                                        style={{ marginRight: "6px" }}
                                      />
                                      Retry Loading PDF
                                    </button>
                                  </div>
                                  <div className="question-text-fallback">
                                    <p>
                                      {question.question_text
                                        ? question.question_text.substring(
                                            0,
                                            300
                                          ) + "..."
                                        : "Question text not available"}
                                    </p>
                                  </div>
                                </div>
                              ) : questionPdfUrls[question.question_id] ? (
                                <div className="pdf-container">
                                  <object
                                    data={questionPdfUrls[question.question_id]}
                                    type="application/pdf"
                                    width="100%"
                                    height="500px"
                                    style={{
                                      border: "1px solid #ddd",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                      <p style={{ marginBottom: '10px' }}>PDF viewer not available in your browser.</p>
                                      <p style={{ marginBottom: '15px' }}>Please use the "Refresh PDF" button above or download the PDF to view it.</p>
                                      <a href={questionPdfUrls[question.question_id]} download={`question_${question.question_number}.pdf`} 
                                         style={{ color: '#007bff', textDecoration: 'underline' }}>
                                        Download PDF
                                      </a>
                                    </div>
                                  </object>
                                  <div className="pdf-actions">
                                    <button
                                      onClick={() =>
                                        loadQuestionPdf(
                                          question.question_id,
                                          true
                                        )
                                      }
                                      className="btn btn-primary btn-sm"
                                      disabled={
                                        loadingPdfs[question.question_id]
                                      }
                                    >
                                      <FontAwesomeIcon
                                        icon={faSyncAlt}
                                        spin={loadingPdfs[question.question_id]}
                                        style={{ marginRight: "6px" }}
                                      />
                                      Refresh PDF
                                    </button>
                                    <a
                                      href={
                                        questionPdfUrls[question.question_id]
                                      }
                                      download={`question_${question.question_number}.pdf`}
                                      className="btn btn-secondary btn-sm"
                                    >
                                      <FontAwesomeIcon icon={faDownload} style={{ marginRight: "6px" }}/>
                                      Download PDF
                                    </a>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() =>
                                    loadQuestionPdf(question.question_id)
                                  }
                                  className="load-pdf-btn"
                                >
                                  <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: "6px" }}/> Load
                                  Question PDF
                                </button>
                              )}
                            </div>

                            <div className="rubric-scores">
                              <h5>Quality Assessment:</h5>
                              <div className="scores-grid">
                                {question.rubric_assessment &&
                                  Object.entries(
                                    question.rubric_assessment
                                  ).map(([key, value]) => {
                                    if (key.endsWith("_justification"))
                                      return null;

                                    // Handle different value types safely
                                    let score = 0;
                                    let displayValue = value;
                                    let justification =
                                      "No justification available";

                                    if (
                                      typeof value === "object" &&
                                      value !== null
                                    ) {
                                      if ("score" in value) {
                                        score =
                                          parseInt(value.score.split("/")[0]) ||
                                          0;
                                        displayValue = value.score;
                                      }
                                      if (
                                        "justification" in value &&
                                        value.justification
                                      ) {
                                        justification = value.justification;
                                      }
                                    } else if (
                                      typeof value === "string" &&
                                      value.includes("/")
                                    ) {
                                      score =
                                        parseInt(value.split("/")[0]) || 0;
                                      displayValue = value;
                                    } else if (typeof value === "number") {
                                      score = value;
                                      displayValue = `${value}/3`;
                                    } else {
                                      displayValue = "N/A";
                                    }

                                    return (
                                      <div key={key} className="score-item">
                                        <div className="score-header">
                                          <span className="score-label">
                                            {key.charAt(0).toUpperCase() +
                                              key.slice(1)}
                                          </span>
                                          <span className="score-value">
                                            {displayValue}
                                          </span>
                                        </div>
                                        <p className="score-justification">
                                          <JustificationWithToggle
                                            text={justification}
                                          />
                                        </p>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>

                            <div className="strengths-weaknesses">
                              <div className="strengths">
                                <h5>Strengths:</h5>
                                <ul>
                                  {Array.isArray(question.strengths) ? (
                                    question.strengths.map(
                                      (strength, index) => (
                                        <li key={index}>{strength}</li>
                                      )
                                    )
                                  ) : (
                                    <li>No strengths identified</li>
                                  )}
                                </ul>
                              </div>
                              <div className="weaknesses">
                                <h5>Areas for Improvement:</h5>
                                <ul>
                                  {Array.isArray(question.weaknesses) ? (
                                    question.weaknesses.map(
                                      (weakness, index) => (
                                        <li key={index}>{weakness}</li>
                                      )
                                    )
                                  ) : (
                                    <li>No areas for improvement identified</li>
                                  )}
                                </ul>
                              </div>
                            </div>

                            {/* Review Interface - Below question content */}
                            <div className="review-section">
                              <h4>
                                <FontAwesomeIcon icon={faClipboardCheck} style={{ marginRight: "6px" }}/>
                                Your Review
                              </h4>

                              <div className="review-options">
                                <button
                                  className={`review-option ${
                                    isAgreed ? "selected agree" : ""
                                  }`}
                                  onClick={() =>
                                    handleQuestionReview(
                                      question.question_id,
                                      question.question_number,
                                      true
                                    )
                                  }
                                >
                                  <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "6px" }}/>
                                  Agree with Analysis
                                </button>

                                <button
                                  className={`review-option ${
                                    isDisagreed ? "selected disagree" : ""
                                  }`}
                                  onClick={() =>
                                    handleQuestionReview(
                                      question.question_id,
                                      question.question_number,
                                      false
                                    )
                                  }
                                >
                                  <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: "6px" }}/>
                                  Disagree with Analysis
                                </button>
                              </div>

                              {(isAgreed || isDisagreed) && (
                                <div className="feedback-input">
                                  <label>
                                    {isDisagreed ? "Your feedback:" : "Additional comments (optional):"}
                                  </label>
                                  <textarea
                                    value={review.feedback || ""}
                                    onChange={(e) => {
                                      const feedback = e.target.value;
                                      // Update local state immediately
                                      setQuestionReviews((prev) => ({
                                        ...prev,
                                        [question.question_id]: {
                                          ...prev[question.question_id],
                                          feedback,
                                        },
                                      }));
                                      // Save to backend with debounce
                                      debouncedSave(
                                        question.question_id,
                                        question.question_number,
                                        feedback
                                      );
                                    }}
                                    onBlur={(e) => {
                                      // Save immediately when user clicks away
                                      handleQuestionFeedbackBlur(
                                        question.question_id,
                                        question.question_number,
                                        e.target.value
                                      );
                                    }}
                                    placeholder={
                                      isDisagreed
                                        ? "Provide your detailed feedback explaining your disagreement..."
                                        : "Add any additional comments or observations (optional)..."
                                    }
                                    rows={4}
                                    className="feedback-textarea"
                                  />
                                  {savingQuestionFeedback[question.question_id] && (
                                    <div className="saving-indicator">
                                      <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
                                      <span>Saving...</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p>No question analysis available.</p>
                )}
              </div>
            </div>

            {/* Paper-level Review */}
            <div className="paper-review-section">
              <h3>
                <FontAwesomeIcon icon={faCommentDots} style={{ marginRight: "6px" }}/>
                Overall Paper Review
              </h3>
              <textarea
                value={paperLevelFeedback}
                onChange={(e) => {
                  const feedback = e.target.value;
                  setPaperLevelFeedback(feedback);
                  
                  // Auto-save with debounce (1.5 seconds)
                  if (savingPaperFeedbackTimeout.current) {
                    clearTimeout(savingPaperFeedbackTimeout.current);
                  }
                  savingPaperFeedbackTimeout.current = setTimeout(() => {
                    debouncedSavePaperFeedback(feedback);
                  }, 1500);
                }}
                onBlur={(e) => {
                  // Save immediately when user clicks away
                  if (savingPaperFeedbackTimeout.current) {
                    clearTimeout(savingPaperFeedbackTimeout.current);
                    savingPaperFeedbackTimeout.current = null;
                  }
                  if (e.target.value && e.target.value.trim() !== "") {
                    debouncedSavePaperFeedback(e.target.value);
                  }
                }}
                placeholder="Provide overall feedback for the entire question paper..."
                rows={6}
                className="paper-feedback-textarea"
              />
              {savingPaperFeedback && (
                <div className="saving-indicator">
                  <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
                  <span>Auto-saving...</span>
                </div>
              )}

              {error && (
                <div className="review-error-message">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="error-icon"
                    style={{ marginRight: "6px" }}
                  />
                  {error}
                </div>
              )}
              <div className="paper-review-actions">
                <button
                  className="btn btn-secondary"
                  onClick={handleDownloadReportPdf}
                  disabled={downloadingReport}
                >
                  {downloadingReport ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
                      Preparing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faDownload} style={{ marginRight: "6px" }}/>
                      Download PDF Report
                    </>
                  )}
                </button>

                <button
                  className="btn btn-primary"
                  onClick={handleSavePaperFeedback}
                  disabled={savingReview || downloadingReport}
                >
                  {savingReview ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
                      Saving Review...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "6px" }}/>
                      Save Complete Review
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Review;
